import { forwardRef, RefObject } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  useBoolean,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { EyeOutline, EyeSlashOutline, useToast, CheckOutline } from '@aslanpay/portal-common-lib';

import useAuthContext from 'app/hooks/useAuthContext';
import useChangePassword from 'app/hooks/useChangePassword';

import { changePasswordFormSchema } from 'app/utils/schemas';

import { ChangePasswordFormFields } from './types';

const ChangePasswordForm = forwardRef<HTMLDivElement>((_, ref) => {
  const { cognitoUser } = useAuthContext();

  const toast = useToast();

  const [showCurrentPassword, setShowCurrentPassword] = useBoolean();
  const [showNewPassword, setShowNewPassword] = useBoolean();
  const [showConfirmPassword, setShowConfirmPassword] = useBoolean();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<ChangePasswordFormFields>({
    mode: 'onBlur',
    resolver: yupResolver(changePasswordFormSchema),
  });

  const { mutate: changePassword, isLoading: isChangingPassword } = useChangePassword({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Password changed',
        description: 'Your password was succesfully changed.',
      });

      reset();
    },
    onError: (error) => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: error.message,
      });
    },
  });

  const handleChangePassword = (data: ChangePasswordFormFields) => {
    changePassword({
      cognitoUser,
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  return (
    <form id="change-password" onSubmit={handleSubmit(handleChangePassword)} noValidate>
      <Flex flexDir="column" gap="20px">
        <FormControl isInvalid={!!errors?.currentPassword?.message} isRequired>
          <FormLabel>Current password</FormLabel>
          <InputGroup>
            <Input
              type={showCurrentPassword ? 'text' : 'password'}
              placeholder="Enter your current password"
              {...register('currentPassword')}
            />
            <InputRightElement
              w="56px"
              h="full"
              cursor="pointer"
              onClick={setShowCurrentPassword.toggle}
            >
              {showCurrentPassword ? (
                <EyeOutline color="brand.mutedPurple" boxSize="24px" />
              ) : (
                <EyeSlashOutline color="brand.mutedPurple" boxSize="24px" />
              )}
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{errors?.currentPassword?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.newPassword?.message} isRequired>
          <FormLabel>New password</FormLabel>
          <InputGroup>
            <Input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Enter your new password"
              {...register('newPassword')}
            />
            <InputRightElement
              w="56px"
              h="full"
              cursor="pointer"
              onClick={setShowNewPassword.toggle}
            >
              {showNewPassword ? (
                <EyeOutline color="brand.mutedPurple" boxSize="24px" />
              ) : (
                <EyeSlashOutline color="brand.mutedPurple" boxSize="24px" />
              )}
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{errors?.newPassword?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.confirmPassword?.message} isRequired>
          <FormLabel>Confirm password</FormLabel>
          <InputGroup>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your new password"
              {...register('confirmPassword')}
            />
            <InputRightElement
              w="56px"
              h="full"
              cursor="pointer"
              onClick={setShowConfirmPassword.toggle}
            >
              {showConfirmPassword ? (
                <EyeOutline color="brand.mutedPurple" boxSize="24px" />
              ) : (
                <EyeSlashOutline color="brand.mutedPurple" boxSize="24px" />
              )}
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{errors?.confirmPassword?.message}</FormErrorMessage>
        </FormControl>
      </Flex>
      <Portal containerRef={ref as RefObject<HTMLElement>}>
        <Flex gap="20px" alignItems="flex-start">
          <Button variant="outline" size="sm" onClick={() => reset()} isDisabled={!isDirty}>
            Cancel
          </Button>
          <Button
            leftIcon={<CheckOutline boxSize="20px" />}
            size="sm"
            type="submit"
            form="change-password"
            isLoading={isChangingPassword}
            isDisabled={!isValid}
          >
            Save changes
          </Button>
        </Flex>
      </Portal>
    </form>
  );
});

ChangePasswordForm.displayName = 'ChangePasswordForm';

export default ChangePasswordForm;
